package com.htmlagent.site.components.sections.landing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.htmlagent.site.components.sections.Glyph
import com.htmlagent.site.components.widgets.prefersReducedMotion
import com.htmlagent.site.components.widgets.reveal
import com.htmlagent.site.i18n.LandingStrings
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.state.rememberNarrowScreen
import com.htmlagent.site.theme.HeroActionsStyle
import com.htmlagent.site.theme.Icons
import com.htmlagent.site.theme.LandingLeadStyle
import com.htmlagent.site.theme.PillPrimaryVariant
import com.htmlagent.site.theme.STEP_PANEL_FROM
import com.htmlagent.site.theme.StepAccordionStyle
import com.htmlagent.site.theme.StepChevronStyle
import com.htmlagent.site.theme.StepGlowStyle
import com.htmlagent.site.theme.StepIconStyle
import com.htmlagent.site.theme.StepKickerStyle
import com.htmlagent.site.theme.StepMarkStyle
import com.htmlagent.site.theme.StepPanelInkStyle
import com.htmlagent.site.theme.StepPanelStyle
import com.htmlagent.site.theme.StepPanelTitleStyle
import com.htmlagent.site.theme.StepRailFillStyle
import com.htmlagent.site.theme.StepRailStyle
import com.htmlagent.site.theme.StepRowBodyStyle
import com.htmlagent.site.theme.StepRowHeadStyle
import com.htmlagent.site.theme.StepRowStyle
import com.htmlagent.site.theme.StepTabStyle
import com.htmlagent.site.theme.StepTabTitleStyle
import com.htmlagent.site.theme.StepTabsStyle
import com.htmlagent.site.theme.StepTitleStyle
import com.htmlagent.site.theme.TagRowStyle
import com.htmlagent.site.theme.TagStyle
import com.htmlagent.site.theme.Tokens
import com.htmlagent.site.theme.labelled
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement
import kotlin.js.json

/**
 * One of the three things that happen when you ask for a page.
 *
 * It is built from the texts rather than declared as data with the texts inside it, so a step
 * cannot exist in one language and not the other: the compiler already refuses that.
 */
private data class Step(
    val kicker: String,
    val title: String,
    val body: String,
    /** The title a waiting step wears: the same promise, short enough for a tab. */
    val short: String,
    /** The same step in a sentence, for when it is only waiting. */
    val hint: String,
    val tags: List<String>,
    /** What happens in it, drawn: you say it, you watch it, you take it. */
    val icon: String,
)

private fun stepsOf(t: LandingStrings): List<Step> = listOf(
    Step(
        t.stepOneKicker, t.stepOneTitle, t.stepOneBody, t.stepOneShort, t.stepOneHint,
        t.stepOneTags, Icons.Chat,
    ),
    Step(
        t.stepTwoKicker, t.stepTwoTitle, t.stepTwoBody, t.stepTwoShort, t.stepTwoHint,
        t.stepTwoTags, Icons.Eye,
    ),
    Step(
        t.stepThreeKicker, t.stepThreeTitle, t.stepThreeBody, t.stepThreeShort, t.stepThreeHint,
        t.stepThreeTags, Icons.Download,
    ),
)

/**
 * How it works: three steps in order, one of them being read.
 *
 * Two shapes, and the reason is that the pieces are the same but the room is not. On a desktop
 * the three are tabs over one panel: only the panel's writing changes, and it arrives from the
 * side the tab it came from is on. On a phone they are an accordion that claims the height of
 * the screen, so the step being read fills it instead of sitting in a strip.
 *
 * What both shapes avoid is the swap the section used to do, where the open card and a waiting
 * one traded places: all three had to fade their text out and back in on every press, which read
 * as the whole band blinking. Nothing changes place now, so nothing has to be hidden.
 */
@Composable
fun LandingSteps(onStart: () -> Unit) {
    val t = LocalStrings.current
    val steps = remember(t) { stepsOf(t.landing) }
    val narrow = rememberNarrowScreen()

    // [index] is the step being read; [entered] only flips so the panel's animation has a new
    // name to play, since its element stays in place from one step to the next.
    var index by remember { mutableStateOf(0) }
    var entered by remember { mutableStateOf(false) }
    var fromRight by remember { mutableStateOf(true) }
    var pressed by remember { mutableStateOf(false) }

    Band(SECTION_STEPS) {
        SectionHead(
            t.landing.stepsEyebrow,
            t.landing.stepsTitle,
            headingId = headingId(SECTION_STEPS),
        )

        if (narrow) {
            StepsAccordion(
                steps = steps,
                index = index,
                onOpen = { picked ->
                    index = picked
                    // Only a press scrolls: the section mounting must not move the page.
                    pressed = true
                },
                pressed = pressed,
                onStart = onStart,
            )
        } else {
            StepsTabs(
                steps = steps,
                index = index,
                entered = entered,
                fromRight = fromRight,
                onPick = { picked ->
                    if (picked != index) {
                        fromRight = picked > index
                        entered = !entered
                        index = picked
                    }
                },
                onStart = onStart,
            )
        }
    }
}

/** The desktop shape: three tabs, a rail that follows them, and the panel they share. */
@Composable
private fun StepsTabs(
    steps: List<Step>,
    index: Int,
    entered: Boolean,
    fromRight: Boolean,
    onPick: (Int) -> Unit,
    onStart: () -> Unit,
) {
    val t = LocalStrings.current

    Div(StepTabsStyle.toModifier().reveal().toAttrs { attr("role", "tablist") }) {
        steps.forEachIndexed { position, step ->
            Button(
                onClick = { onPick(position) },
                modifier = StepTabStyle.toModifier()
                    .labelled(t.landing.stepsOpen(step.kicker.lowercase()))
                    .attrsModifier {
                        attr("role", "tab")
                        attr("aria-selected", (position == index).toString())
                        attr("data-on", (position == index).toString())
                    },
                variant = null,
            ) {
                Mark(step.icon, on = position == index)
                Div(attrs = null) {
                    Span(StepKickerStyle.toModifier().toAttrs()) { Text(step.kicker) }
                    Span(StepTabTitleStyle.toModifier().toAttrs()) { Text(step.short) }
                }
            }
        }
    }

    Div(StepRailStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) {
        Div(
            StepRailFillStyle.toModifier()
                .styleModifier { property("transform", "translateX(${index * RAIL_STEP}%)") }
                .toAttrs(),
        )
    }

    Div(StepPanelStyle.toModifier().toAttrs { attr("role", "tabpanel") }) {
        Div(StepGlowStyle.toModifier().toAttrs { attr("aria-hidden", "true") })
        Div(StepIconStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) {
            Glyph(steps[index].icon, PANEL_ICON)
        }
        Div(
            StepPanelInkStyle.toModifier()
                .styleModifier {
                    // The name alternates because the element does not: a repeat of the same
                    // name on the same node plays nothing.
                    property(
                        "animation-name",
                        if (entered) "ha-step-panel-b" else "ha-step-panel-a",
                    )
                    property(STEP_PANEL_FROM, if (fromRight) "$PANEL_TRAVEL" else "-$PANEL_TRAVEL")
                }
                .toAttrs(),
        ) {
            Span(StepKickerStyle.toModifier().toAttrs()) { Text(steps[index].kicker) }
            StepBody(steps[index], onStart, title = { step ->
                H3(StepPanelTitleStyle.toModifier().toAttrs()) { Text(step.title) }
            })
        }
    }
}

/** The phone's shape: the three in a column, the open one filling what the others leave. */
@Composable
private fun StepsAccordion(
    steps: List<Step>,
    index: Int,
    onOpen: (Int) -> Unit,
    pressed: Boolean,
    onStart: () -> Unit,
) {
    val t = LocalStrings.current

    // The row that just opened is brought into view, and the wait is two frames on purpose: the
    // first is the one Compose paints the new state in, and only after it has the row the height
    // this needs to read. Measuring in the same frame gave a delta of zero, which is the mistake
    // the section carried written down for weeks.
    LaunchedEffect(index, pressed) {
        if (!pressed) return@LaunchedEffect
        window.requestAnimationFrame {
            window.requestAnimationFrame { showRow(index) }
        }
    }

    Div(StepAccordionStyle.toModifier().reveal().toAttrs()) {
        steps.forEachIndexed { position, step ->
            val open = position == index
            Div(
                StepRowStyle.toModifier().toAttrs {
                    attr("data-open", open.toString())
                    attr("data-step", position.toString())
                },
            ) {
                Button(
                    onClick = { onOpen(position) },
                    modifier = StepRowHeadStyle.toModifier()
                        .labelled(t.landing.stepsOpen(step.kicker.lowercase()))
                        .attrsModifier { attr("aria-expanded", open.toString()) },
                    variant = null,
                ) {
                    Mark(step.icon, on = open)
                    Div {
                        Span(StepKickerStyle.toModifier().toAttrs()) { Text(step.kicker) }
                        Span(StepTabTitleStyle.toModifier().toAttrs()) { Text(step.short) }
                    }
                    Div(
                        StepChevronStyle.toModifier()
                            .styleModifier {
                                property("transform", if (open) "rotate(90deg)" else "none")
                            }
                            .toAttrs { attr("aria-hidden", "true") },
                    ) {
                        Glyph(Icons.ArrowRight, Tokens.iconSm.value())
                    }
                }

                if (open) {
                    Div(StepRowBodyStyle.toModifier().toAttrs()) {
                        StepBody(step, onStart, title = { openStep ->
                            H3(StepTitleStyle.toModifier().toAttrs()) { Text(openStep.title) }
                        })
                    }
                }
            }
        }
    }
}

/** What a step says once it is the one being read, whichever shape is showing it. */
@Composable
private fun StepBody(step: Step, onStart: () -> Unit, title: @Composable (Step) -> Unit) {
    val t = LocalStrings.current

    title(step)
    P(LandingLeadStyle.toModifier().toAttrs()) { Text(step.body) }

    Ul(TagRowStyle.toModifier().toAttrs()) {
        step.tags.forEach { tag ->
            Li(TagStyle.toModifier().toAttrs()) { Text(tag) }
        }
    }

    Div(HeroActionsStyle.toModifier().toAttrs()) {
        Button(onClick = { onStart() }, variant = PillPrimaryVariant) {
            Text(t.landing.stepsCta)
            Glyph(Icons.ArrowRight, Tokens.iconSm.value())
        }
    }
}

/** The step's mark, quiet while it waits and in the accent while it is being read. */
@Composable
private fun Mark(icon: String, on: Boolean) {
    Div(
        StepMarkStyle.toModifier().toAttrs {
            attr("aria-hidden", "true")
            attr("data-on", on.toString())
        },
    ) {
        Glyph(icon, Tokens.iconSm.value())
    }
}

/** The drawing behind the panel, big enough to read as an illustration and not as an icon. */
private val PANEL_ICON = 132.px

/** One third of the rail, in the percent its own width is measured in. */
private const val RAIL_STEP = 100

/** How far the panel's writing travels on its way in. */
private const val PANEL_TRAVEL = "48px"

/**
 * Puts the open row where it can be read whole.
 *
 * Centred while it fits in the window, and against the top when it does not: a tall step centred
 * would start above the bar, and its first line is the one that matters.
 */
private fun showRow(index: Int) {
    val row = document.querySelector(".step-row[data-step=\"$index\"]") as? HTMLElement ?: return
    val room = window.innerHeight - NAV_ROOM
    val block = if (row.offsetHeight + BREATHING_ROOM <= room) "center" else "start"
    val behavior = if (prefersReducedMotion()) "auto" else "smooth"
    row.asDynamic().scrollIntoView(json("block" to block, "behavior" to behavior, "inline" to "nearest"))
}

/** What the sticky bar and its margin take off the window's height. */
private const val NAV_ROOM = 96

/** The air a centred row needs above and below it to read as centred at all. */
private const val BREATHING_ROOM = 48
